import React, { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';

import { GTMTracking } from '@common/ui/general';
import { useMediaQuery } from '@common/hooks';

import { updateRequestParams } from '@alumni_v2/stores/reviews/actions';
import { requestParamsSelector } from '@alumni_v2/selectors/reviews';

import { getPreSelectedFilters } from '@alumni_v2/utils/getPreSelectedFilters';
import setPreSelectedFilters,
{ setPreSelectedAdvancedFilters }
  from '@alumni_v2/utils/setPreSelectedFilters';

import AdvancedFiltersMobile from '../AdvancedFiltersMobile';

import closeIcon from '@alumni_v2/images/svg/cross-icon.svg';
import filterIcon from '@alumni_v2/images/svg/funnel.svg';
import yellowDot from '@alumni_v2/images/svg/yellowDot.svg';

import styles from './QuickFilters.module.scss';

const DSML_QUICK_FILTER = 'data_science_&_ml';
const DSML_URL_PATH = '/data-science-course/reviews/';
const AIML_QUICK_FILTER = 'artificial_intelligence_&_ml';
const AIML_URL_PATH = '/ai-machine-learning-course/reviews/';
const REVIEW_URL_PATH = '/review/';

const QuickFilters = ({
  selectedFilters,
  setSelectedFilters,
  experienceFilter,
  setExperienceFilter,
  salaryHikeFilter,
  setSalaryHikeFilter,
  advancedFiltersMobile,
  setAdvancedFiltersMobile,
}) => {
  const dispatch = useDispatch();
  const { mobile } = useMediaQuery();
  const { quickFilters } = window.__INITIAL_STATE__;
  const [isPopUpVisible, setIsPopUpVisible] = useState(false);
  const requestParams = useSelector(requestParamsSelector) || {};

  const setPageUrl = useCallback((currentFilters, sharedFilter) => {
    setPreSelectedFilters(currentFilters);
    setPreSelectedAdvancedFilters(sharedFilter);

    if (currentFilters.includes(DSML_QUICK_FILTER)) {
      if (!window.location.pathname.includes(DSML_URL_PATH)) {
        window.location.href = DSML_URL_PATH;
      }
    } else if (currentFilters.includes(AIML_QUICK_FILTER)) {
      if (!window.location.pathname.includes(AIML_URL_PATH)) {
        window.location.href = AIML_URL_PATH;
      }
    } else if (!window.location.pathname.includes(REVIEW_URL_PATH)) {
      window.location.href = REVIEW_URL_PATH;
    }
  }, []);

  const handleFilterChange = useCallback((filterValue) => {
    const { sharedFilter } = getPreSelectedFilters();

    setSelectedFilters((prevFilters) => {
      let updatedFilters = prevFilters.includes(filterValue)
        ? prevFilters.filter((filter) => filter !== filterValue)
        : [...prevFilters, filterValue];

      if (filterValue === DSML_QUICK_FILTER) {
        updatedFilters = updatedFilters.filter(
          (filter) => filter !== AIML_QUICK_FILTER,
        );
      } else if (filterValue === AIML_QUICK_FILTER) {
        updatedFilters = updatedFilters.filter(
          (filter) => filter !== DSML_QUICK_FILTER,
        );
      }

      if (updatedFilters.includes('2_to_5_years_experience')) {
        setExperienceFilter({ min: 2, max: 5 });
      }

      setPageUrl(updatedFilters, sharedFilter);

      dispatch(
        updateRequestParams(
          [...updatedFilters],
          sharedFilter,
          null,
          false,
          true,
        ),
      );

      return updatedFilters;
    });
  }, [dispatch, setExperienceFilter, setPageUrl, setSelectedFilters]);

  useEffect(() => {
    const { preSelectedFiltersJson, sharedFilter } = getPreSelectedFilters();

    dispatch(updateRequestParams(
      preSelectedFiltersJson, sharedFilter, null, true, true,
    ));
    setSelectedFilters(preSelectedFiltersJson);

    if (sharedFilter?.experience) {
      setExperienceFilter({
        min: sharedFilter?.experience[0]?.min,
        max: sharedFilter?.experience[0]?.max,
      });
    }

    if (sharedFilter?.growth) {
      setSalaryHikeFilter({
        min: sharedFilter?.growth[0]?.min,
        max: sharedFilter?.growth[0]?.max,
      });
    }
  }, [
    dispatch, setExperienceFilter, setPageUrl,
    setSalaryHikeFilter, setSelectedFilters]);

  return (
    <div className={styles.quickFilterWrapper}>
      <div className={styles.container}>
        <div className={styles.filtersWrapper}>
          <div className={styles.heading}>
            {
              mobile ? (
                <GTMTracking
                  className={styles.filterButtonWrapper}
                  component="button"
                  type="button"
                  onClick={() => setIsPopUpVisible(true)}
                  gtmEventType="cta"
                  gtmEventAction="click"
                  gtmEventResult="open_filter_pane"
                  gtmEventCategory="alumni_directory"
                  gtmEventData={{
                    click_type: 'open_filter_pane',
                    click_text: 'Filters',
                    click_section: 'filter_section',
                  }}
                >
                  <div className={styles.filterButton}>
                    <img src={filterIcon} alt="filterIcon" />
                    {
                      (requestParams.search
                      || requestParams.quickFilters?.length
                      || Object.keys(
                        requestParams.advancedFilters || {},
                      ).length > 0)
                      && (
                        <img
                          className={styles.yellowDot}
                          src={yellowDot}
                          alt="yellowDot"
                        />
                      )
                    }
                    <div className={styles.filterButtonText}>
                      Filters
                    </div>
                  </div>
                </GTMTracking>
              ) : (
                'Quick Filters:'
              )
            }
          </div>
          <div className={styles.filtersContainer}>
            {quickFilters.map((filter, index) => {
              const isSelected = selectedFilters?.includes(filter.value);

              return (
                <GTMTracking
                  key={filter.value}
                  component="div"
                  gtmEventType="cta"
                  gtmEventAction="click"
                  gtmEventResult={isSelected
                    ? 'clear_quick_filter' : 'select_quick_filter'}
                  gtmEventCategory="alumni_directory"
                  gtmEventData={{
                    click_type: isSelected
                      ? 'clear_quick_filter' : 'select_quick_filter',
                    click_text: filter.label,
                    click_section: 'quick_filter_section',
                    gtm_custom_attributes: {
                      element_position: index + 1,
                    },
                  }}
                  className={classNames(styles.quickFilters, {
                    [styles.selectedFilter]: isSelected,
                  })}
                  onClick={() => handleFilterChange(filter.value)}
                >
                  {filter.label}
                  {isSelected && (
                    <img
                      src={closeIcon}
                      width={16}
                      height={16}
                      alt="close icon"
                    />
                  )}
                </GTMTracking>
              );
            })}
          </div>
        </div>
      </div>
      {
        mobile && (
          <AdvancedFiltersMobile
            isPopUpVisible={isPopUpVisible}
            setIsPopUpVisible={setIsPopUpVisible}
            experienceFilter={experienceFilter}
            setExperienceFilter={setExperienceFilter}
            salaryHikeFilter={salaryHikeFilter}
            setSalaryHikeFilter={setSalaryHikeFilter}
            selectedFilters={advancedFiltersMobile}
            setSelectedFilters={setAdvancedFiltersMobile}
          />
        )
      }
    </div>
  );
};

export default QuickFilters;
